import { useContext } from "react";
import { Typography } from "@mui/material";
import { UserModuleContext } from "../../context/UserModuleContext";
import { formatDate } from "../../utils/FormatDate";
import { usePersonInfo } from "./usePersonInfo";

const SECTIONS = [
    { heading: "Identity", fields: [
            { label: "Network ID", key: "Network ID" },
            { label: "WSU Number", key: "WSU Number" },
            { label: "Display Name", key: "Display Name" },
            { label: "External System ID", key: "External System ID" },
            { label: "Okta ID", key: "Okta ID" },
        ]},
    { heading: "Contact", fields: [
            { label: "Primary Email", key: "Primary Email" },
            { label: "Secondary Email", key: "Secondary Email" },
        ]},
    { heading: "Roles & Affiliation", fields: [
            { label: "Affiliation", key: "Affiliation" },
            { label: "Employee Role", key: "Employee Role" },
            { label: "Instructor Type", key: "Instructor Type" },
            { label: "Affiliate Role Type", key: "Affiliate Role Type" },
            { label: "Former Employee", key: "Former Employee" },
            { label: "Former Student", key: "Former Student" },
            { label: "Student Program Status", key: "Student Program Status" },
        ]},
    { heading: "Management", fields: [
            { label: "Manager", key: "Manager" },
            { label: "Manager ID", key: "ManagerId" },
        ]},
    { heading: "Directory & Privacy", fields: [
            { label: "Student Name and Location Unlisted", key: "Student Name and Location Unlisted" },
            { label: "Student Directory Enabled", key: "Student Directory Enabled" },
            { label: "Special Hide From GAL", key: "Special Hide From GAL" },
            { label: "FERPA Trained", key: "FERPA Trained" },
        ]},
    { heading: "Okta & Authentication", fields: [
            { label: "Okta Status", key: "Okta Status" },
            { label: "AD Sync Status", key: "AD Sync Status" },
            { label: "MFA Status", key: "MFA Status" },
            { label: "Required MFA Preference", key: "Required MFA Preference" },
        ]},
    { heading: "Dates", fields: [
            { label: "Created", key: "Created", format: "date" },
            { label: "Activated", key: "Activated", format: "date" },
            { label: "Last Login", key: "Last Login", format: "date" },
            { label: "Password Changed", key: "Password Changed", format: "date" },
        ]},
];

const resolveValue = (userData, key, format) => {
    const raw = userData[key];
    if (raw === null || raw === undefined) return null;
    if (format === "date") return formatDate(raw);
    return raw.toString();
};

const renderCell = (value) =>
    value ? (
        <Typography variant="body2">{value}</Typography>
    ) : (
        <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>
    );

// Returns one "tile" per section, each carrying the length of its longest
// value so the grid can decide how much width the tile needs.
export const usePersonSections = (identifier) => {
    const { isLoaded, error } = usePersonInfo(identifier);
    const { userModule } = useContext(UserModuleContext);

    if (!isLoaded || error) {
        return { isLoaded, error, tiles: [] };
    }

    const userData = userModule.userData || {};

    const tiles = SECTIONS.map((section) => {
        const rows = section.fields.map(({ label, key, format }) => {
            const value = resolveValue(userData, key, format);
            return { key, label, content: renderCell(value), length: value ? value.length : 0 };
        });
        return {
            key: section.heading,
            heading: section.heading,
            rows,
            maxLength: rows.reduce((max, row) => Math.max(max, row.length), 0),
        };
    });

    return { isLoaded, error, tiles };
};
